import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** Keeps Horizon's gallery selection and native modal focus/scroll behaviour. */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #controller;
  #index = 0;
  #opener;
  #scrollY = 0;
  #loadingImages = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('photo-navigate', (event) => this.#select(this.#index + event.detail), options);
    this.addEventListener('photo-zoom', (event) => {
      if (event.target !== this.#wrapper) return;
      this.#updateControls();
      if (event.detail > 1) this.#loadHighResolutionImage();
    }, options);
    this.refs.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
    this.refs.dialog.addEventListener('close', this.#afterClose, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  get #wrapper() { return this.refs.media[this.#index]?.querySelector('drag-zoom-wrapper'); }

  open(index, event) {
    event?.preventDefault();
    if (this.refs.dialog.open) return;
    this.#opener = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    this.#scrollY = window.scrollY;
    this.refs.dialog.showModal();
    this.#select(Number(index));
    this.querySelector('.dialog-zoomed-gallery__close-button')?.focus({ preventScroll: true });
  }

  openFromKeyboard(index, event) {
    if (event.key === 'Enter' || event.key === ' ') this.open(index, event);
  }

  close() { this.refs.dialog.close(); }
  closeDialog() { this.close(); }

  #afterClose = () => {
    this.#wrapper?.reset();
    this.refs.media.forEach((media) => {
      media.querySelectorAll('video').forEach((video) => video.pause());
    });
    window.dispatchEvent(new DialogCloseEvent());
    const gallery = this.closest('media-gallery');
    const selectedSlide = gallery?.querySelectorAll('slideshow-slide')[this.#index];
    const focusTarget = selectedSlide?.hasAttribute('tabindex') ? selectedSlide : this.#opener;
    focusTarget?.focus({ preventScroll: true });
    window.scrollTo({ top: this.#scrollY, behavior: 'instant' });
  };

  previous() { this.#select(this.#index - 1); }
  next() { this.#select(this.#index + 1); }
  zoomIn() { this.#wrapper?.zoomBy(1); }
  zoomOut() { this.#wrapper?.zoomBy(-1); }
  resetZoom() { this.#wrapper?.reset(); }
  handleThumbnailClick(index) { this.#select(Number(index)); }
  selectThumbnail(index) { this.#select(Number(index)); }

  handleKeyDown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) return;
    const wrapper = this.#wrapper;
    const pan = wrapper?.scale > 1;
    const actions = {
      ArrowLeft: () => pan ? wrapper.panBy(80, 0) : this.previous(),
      ArrowRight: () => pan ? wrapper.panBy(-80, 0) : this.next(),
      ArrowUp: () => pan && wrapper.panBy(0, 80),
      ArrowDown: () => pan && wrapper.panBy(0, -80),
      '+': () => this.zoomIn(), '=': () => this.zoomIn(),
      '-': () => this.zoomOut(), '0': () => this.resetZoom(),
    };
    if (actions[event.key]) { event.preventDefault(); actions[event.key](); }
  }

  #select(index) {
    const { media, thumbnails } = this.refs;
    if (!Number.isInteger(index) || index < 0 || index >= media.length) return;
    this.#wrapper?.reset();
    media.forEach((item, i) => {
      item.hidden = i !== index;
      if (i !== index) item.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.#index = index;
    this.#wrapper?.reset();
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', String(i === index));
      button.setAttribute('aria-selected', String(i === index));
    });
    thumbnails.children[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    this.querySelector('[data-photo-count]').textContent = `${index + 1} / ${media.length}`;
    this.#updateControls();
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #updateControls() {
    const wrapper = this.#wrapper;
    const scale = wrapper?.scale ?? 1;
    this.querySelector('[data-photo-previous]').disabled = this.#index === 0;
    this.querySelector('[data-photo-next]').disabled = this.#index === this.refs.media.length - 1;
    this.querySelector('[data-photo-zoom-in]').disabled = !wrapper || scale >= 5;
    this.querySelector('[data-photo-zoom-out]').disabled = !wrapper || scale <= 1;
    this.querySelector('[data-photo-reset]').disabled = !wrapper || scale === 1;
  }

  #loadHighResolutionImage() {
    const image = this.#wrapper?.querySelector('img');
    const url = image?.getAttribute('data_max_resolution');
    if (!url || this.#loadingImages.has(url)) return;
    this.#loadingImages.add(url);
    const highRes = new Image();
    highRes.onload = () => {
      highRes.className = image.className;
      highRes.alt = image.alt;
      highRes.setAttribute('data_max_resolution', url);
      image.replaceWith(highRes);
    };
    highRes.onerror = () => this.#loadingImages.delete(url);
    highRes.src = url;
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
